// Plain-English copy for the household member-role control.
//
// Pure + import-light on purpose (only the shared role vocabulary), so
// the wording is unit-testable in the node pool rather than only
// reachable by rendering a page. The copy IS the feature here: Donna
// followed an instruction that described a control nobody had built, so
// every string this module produces has to say exactly what happened or
// exactly what to do instead — never "Saved."

import {
  humanizeHouseholdRole,
  type HouseholdRoleOption,
  HOUSEHOLD_ROLE_OPTIONS,
} from "@shared/lib/household-roles";
import type { HouseholdRole } from "@shared/types/enums";

/**
 * What to say after a successful role change.
 *
 * The promotion case names the DEMOTION too. Designating a primary
 * silently demotes whoever held it — the half of the action the person
 * clicking cannot see coming, and the half that surprised people on the
 * Members tab's "Make primary" before this existed.
 */
export function memberRoleChangeMessage(
  memberName: string,
  toRole: HouseholdRole,
  demotedNames: string[],
): string {
  if (toRole === "primary") {
    const base = `${memberName} is now the primary contact.`;
    if (demotedNames.length === 0) return base;
    if (demotedNames.length === 1) {
      return `${base} ${demotedNames[0]} is now a regular member of the household.`;
    }
    return `${base} ${demotedNames.join(" and ")} are now regular members of the household.`;
  }
  return `${memberName} is now recorded as ${roleWithArticle(toRole)}.`;
}

/**
 * Why the designated primary's role can't be changed here, and what to
 * do instead. Never a bare "not allowed" — the whole point of this
 * change is that a dead end reads as a working instruction.
 */
export function memberRoleRefusalMessage(memberName: string): string {
  return `${memberName} is this household's primary contact. Make someone else the primary first — ${memberName} becomes a regular member automatically.`;
}

/**
 * Options offered for a given member. The designated primary is not
 * offered a demotion at all (the server refuses it), so the picker never
 * shows a choice that can only fail.
 */
export function memberRoleOptions(
  isDesignatedPrimary: boolean,
): ReadonlyArray<HouseholdRoleOption> {
  if (!isDesignatedPrimary) return HOUSEHOLD_ROLE_OPTIONS;
  return HOUSEHOLD_ROLE_OPTIONS.filter((o) => o.value === "primary");
}

/** "a spouse" / "a child" / "an other member" — lowercase, article-correct. */
function roleWithArticle(role: HouseholdRole): string {
  switch (role) {
    case "spouse":
      return "a spouse";
    case "child":
      return "a child";
    case "other":
      return "another household member";
    case "member":
      return "a household member";
    case "primary":
      return "the primary contact";
    default:
      return humanizeHouseholdRole(role).toLowerCase();
  }
}
